// «Отримати телефон» для OLX-объявлений без номера: запрос уходит в очередь
// воркера (24/7, работает и при выключенном ПК), ответ прилетает опросом
// каждые 7 с или сообщением бота в Telegram.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { requestContact, fetchContactState } from '../../services/contact';

interface RequestPhoneProps {
  adId: string;
  /** Ссылка на исходник — показываем в подсказке при скрытом телефоне. */
  sourceUrl?: string;
  /** Телефон получен — родитель показывает обычные кнопки (звонок/WA/Viber). */
  onPhone: (phone: string) => void;
}

type Phase = 'idle' | 'requesting' | 'pending' | 'denied' | 'failed';

const POLL_MS = 7000;

export function RequestPhone({ adId, sourceUrl, onPhone }: RequestPhoneProps) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>('idle');
  const timerRef = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const startPolling = useCallback(
    (poll: () => void) => {
      if (timerRef.current === null) timerRef.current = window.setInterval(poll, POLL_MS);
    },
    [],
  );

  const poll = useCallback(async () => {
    try {
      const state = await fetchContactState(adId);
      if (state.phone) {
        stopPolling();
        onPhone(state.phone);
      } else if (state.denied) {
        stopPolling();
        setPhase('denied');
      }
    } catch {
      // сеть моргнула — следующий тик опроса повторит
    }
  }, [adId, onPhone, stopPolling]);

  // Экран открыли, а запрос уже висит или решён (например, вернулись по ссылке
  // из уведомления бота) — подтягиваем состояние без лишнего POST.
  useEffect(() => {
    let cancelled = false;
    fetchContactState(adId)
      .then((state) => {
        if (cancelled) return;
        if (state.phone) {
          onPhone(state.phone);
          return;
        }
        if (state.denied) {
          setPhase('denied');
          return;
        }
        if (state.pending) {
          setPhase('pending');
          startPolling(() => void poll());
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adId]);

  const handleClick = async () => {
    setPhase('requesting');
    try {
      const res = await requestContact(adId);
      if (res.status === 'fetched') {
        onPhone(res.phone);
        return;
      }
      if (res.status === 'denied') {
        setPhase('denied');
        return;
      }
      setPhase('pending');
      startPolling(() => void poll());
    } catch {
      setPhase('failed');
    }
  };

  if (phase === 'requesting') {
    return (
      <button className="contact-btn contact-btn--request" disabled type="button">
        ⏳
      </button>
    );
  }

  if (phase === 'pending') {
    return <div className="contact-request__note">{t('adDetails.requestPhonePending')}</div>;
  }

  if (phase === 'denied') {
    return (
      <div className="contact-request__note">
        {t('adDetails.requestPhoneDenied')}
        {sourceUrl && (
          <>
            {' '}
            <a href={sourceUrl} target="_blank" rel="noopener noreferrer">
              {t('adDetails.sourceLink')}
            </a>
          </>
        )}
      </div>
    );
  }

  if (phase === 'failed') {
    return (
      <div className="contact-request__note">
        {t('adDetails.requestPhoneFailed')}{' '}
        <button
          className="contact-request__retry"
          type="button"
          onClick={() => setPhase('idle')}
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }

  return (
    <button className="contact-btn contact-btn--request" onClick={handleClick} type="button">
      {t('adDetails.requestPhone')}
    </button>
  );
}
